import { useEffect, useRef, useState } from 'react'

/**
 * BotsCatalogSearch — поиск по вкладкам страницы /bots/images|video
 * (лупа в строке табов «Инструменты | Тренды | Галерея стилей»).
 *
 * Клиентский и мгновенный: индекс собирают страницы (данные всех вкладок
 * уже загружены — секции Популярных + StylesContext), сюда приходят готовые
 * группы результатов. Дропдаун группирует по разделам, клик по результату =
 * штатное применение карточки (onPick группы). Параллельно тот же запрос
 * фильтрует раскрытую сетку активной вкладки (проп searchQuery ниже по
 * дереву): дропдаун — межвкладочные находки, сетка — текущая вкладка.
 *
 * С 2026-08-13 это ЕДИНСТВЕННЫЙ поиск разделов /bots: «Все нейросети» и
 * «ИИ-Помощники» тоже перешли на него (единый стиль навигации, решение
 * владельца) — прежний широкий инпут BotsSearch удалён.
 */

export interface CatalogSearchItem {
	id: string
	title: string
	previewUrl?: string | null
	/** Класс превью-картинки: логотипы моделей (каталог /bots) рисуются
	 *  contain'ом с воздухом (`bcs__thumb--logo`), а монохромным нужен ещё
	 *  `model-icon-dark` — инверсия на тёмной теме. Фото-превью — без класса. */
	previewClass?: string
}

export interface CatalogSearchGroup {
	key: string
	title: string
	items: CatalogSearchItem[]
	onPick: (id: string) => void
}

const MAX_PER_GROUP = 6

const isVideoUrl = (u?: string | null) => !!u && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(u)

export default function BotsCatalogSearch({
	query,
	onQueryChange,
	groups,
	placeholder = 'Поиск по разделам…',
}: {
	query: string
	onQueryChange: (q: string) => void
	groups: CatalogSearchGroup[]
	placeholder?: string
}) {
	const [open, setOpen] = useState(false)
	const rootRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	const active = query.trim().length > 0
	const total = groups.reduce((n, g) => n + g.items.length, 0)

	// Клик-вне прячет дропдаун (фильтр сетки остаётся), Esc сбрасывает всё.
	useEffect(() => {
		if (!open) return
		const onDown = (e: MouseEvent) => {
			if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onQueryChange('')
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onKey)
		}
	}, [open, onQueryChange])

	const expanded = open || active

	return (
		<div className={`bcs${expanded ? ' bcs--open' : ''}`} ref={rootRef}>
			<button
				type="button"
				className="bcs__toggle"
				aria-label={expanded ? 'Закрыть поиск' : 'Поиск по разделам'}
				onClick={() => {
					if (expanded) {
						onQueryChange('')
						setOpen(false)
					} else {
						setOpen(true)
						// Фокус после раскрытия (transition ширины не мешает вводу).
						setTimeout(() => inputRef.current?.focus(), 30)
					}
				}}
			>
				{/* Лупа: симметричные примитивы (круг + ручка), без freehand-путей. */}
				<svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
					<circle cx="6.5" cy="6.5" r="4.75" stroke="currentColor" strokeWidth="1.5" />
					<path d="M10.2 10.2L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
				</svg>
			</button>
			<input
				ref={inputRef}
				className="bcs__input"
				type="text"
				value={query}
				placeholder={placeholder}
				aria-label="Поиск по инструментам, трендам и стилям"
				autoComplete="off"
				spellCheck={false}
				onChange={(e) => onQueryChange(e.target.value)}
				onFocus={() => setOpen(true)}
			/>
			{active && (
				<button
					type="button"
					className="bcs__clear"
					aria-label="Очистить поиск"
					onClick={() => {
						onQueryChange('')
						inputRef.current?.focus()
					}}
				>
					<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
						<path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
					</svg>
				</button>
			)}

			{active && open && (
				<div className="bcs__drop" role="listbox" aria-label="Результаты поиска">
					{total === 0 ? (
						<div className="bcs__empty">
							Ничего не нашли. Попробуйте другое слово — или откройте «Все» в разделе.
						</div>
					) : (
						groups
							.filter((g) => g.items.length > 0)
							.map((g) => (
								<div key={g.key} className="bcs__group">
									<div className="bcs__group-title">
										{g.title}
										<span className="bcs__group-count">{g.items.length}</span>
									</div>
									{g.items.slice(0, MAX_PER_GROUP).map((it) => (
										<button
											key={it.id}
											type="button"
											className="bcs__item"
											role="option"
											aria-selected="false"
											onClick={() => {
												g.onPick(it.id)
												onQueryChange('')
												setOpen(false)
											}}
										>
											<span className="bcs__thumb">
												{it.previewUrl ? (
													isVideoUrl(it.previewUrl) ? (
														<video src={it.previewUrl} muted playsInline preload="metadata" />
													) : (
														<img
															src={it.previewUrl}
															alt=""
															loading="lazy"
															decoding="async"
															className={it.previewClass || undefined}
														/>
													)
												) : null}
											</span>
											<span className="bcs__item-title">{it.title}</span>
										</button>
									))}
									{g.items.length > MAX_PER_GROUP && (
										<div className="bcs__more">ещё {g.items.length - MAX_PER_GROUP} — в сетке ниже</div>
									)}
								</div>
							))
					)}
				</div>
			)}
		</div>
	)
}
